import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { BASE_URL } from '../../apiConfig';
import { getTenantHeaders } from '../../config/tenant';
import { CourseDetails, Lesson } from '../../hooks/useCourseApi';
import axios from 'axios';
import { BookOpen, BookMarked } from 'lucide-react';
import EnrolledCourseCard from './EnrolledCourseCard';
import MyProgressStatsSection from './MyProgressStatsSection';
import {
  getStudentProgressStats,
  getStudentQuizProgress,
  getStudentVideoProgress,
} from '../../services/statisticsService';
import type { QuizProgressStats, StudentProgressStats, VideoProgressStats } from '../../types/statistics';

interface Course {
  id: string;
  courseName: string;
  term: string;
  active: boolean;
  price: number;
  grade: string;
  description: string;
  imagePath: string;
  modificationDate: string;
  progress?: number; // Progress percentage (0-100)
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    courses: Course[];
  };
}

// Centralized request deduplication
const pendingRequests = new Map<string, Promise<any>>();

const EnrolledCourses: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10; // Updated to match API default
  const [overallProgress, setOverallProgress] = useState<VideoProgressStats | null>(null);
  const [quizProgress, setQuizProgress] = useState<QuizProgressStats | null>(null);
  const [studentStats, setStudentStats] = useState<StudentProgressStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [expandedCourseId, setExpandedCourseId] = useState<Set<string>>(new Set());
  const [courseCurriculums, setCourseCurriculums] = useState<Map<string, CourseDetails>>(new Map());
  const [loadingCurriculums, setLoadingCurriculums] = useState<Set<string>>(new Set());
  const [expandedUnits, setExpandedUnits] = useState<Map<string, Set<number>>>(new Map());
  
  // Helper function to construct course image URL
  const getCourseImageSrc = (course: Course) => {
    // If imagePath is null or undefined, use default image
    if (!course.imagePath) {
      return '/default-course.jpg';
    }
    
    if (course.imagePath) {
      // If it's already a full URL, use it as is
      if (course.imagePath.startsWith('http')) {
        return course.imagePath;
      }
      
      // Handle relative paths - remove leading slash to avoid double slashes
      let imagePath = course.imagePath;
      if (imagePath.startsWith('/')) {
        imagePath = imagePath.substring(1); // Remove leading slash
      }
      
      // Ensure BASE_URL ends with a slash
      const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
      return `${baseUrl}${imagePath}`;
    }
    // Fallback to a default image if no image path is provided
    return '/default-course.jpg';
  };

  useEffect(() => {
    fetchCourses();
  }, [currentPage, currentUser?.id]);

  useEffect(() => {
    if (currentUser?.id) {
      fetchProgressStats();
    }
  }, [currentUser?.id]);

  const fetchProgressStats = async () => {
    if (!currentUser?.id) return;
    setLoadingStats(true);
    try {
      const [stats, video, quiz] = await Promise.all([
        getStudentProgressStats(currentUser.id),
        getStudentVideoProgress(currentUser.id),
        getStudentQuizProgress(currentUser.id),
      ]);
      setStudentStats(stats);
      setOverallProgress(video);
      setQuizProgress(quiz);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchCourses = async () => {
    // Create a unique key for this request to prevent duplicates
    const requestKey = `courses_${currentUser?.id}_${currentPage}_${pageSize}`;
    
    // Check if there's already a pending request for this key
    if (pendingRequests.has(requestKey)) {
      console.log('Request already in progress, skipping duplicate call');
      return pendingRequests.get(requestKey);
    }

    const requestPromise = (async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setError('لم يتم العثور على رمز المصادقة. الرجاء تسجيل الدخول مرة أخرى.');
          return;
        }

        console.log('Fetching enrolled courses:', { userId: currentUser?.id, page: currentPage });

        const response = await fetch(
          `${BASE_URL}api/Student/Student-Enrolled-Courses?studentId=${currentUser?.id}&pagenumber=${currentPage}&pagesize=${pageSize}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              ...getTenantHeaders()
            },
          }
        );

        if (!response.ok) {
        if (response.status === 401) {
          setError('انتهت صلاحية الجلسة. الرجاء تسجيل الدخول مرة أخرى.');
          navigate('/login');
          return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ApiResponse = await response.json();
          
        if (data.success && data.data) {
            const coursesList = data.data.courses;
            setCourses(coursesList);
            setTotalPages(data.data.totalPages);
            setTotalCount(data.data.totalCount);
            
            // Fetch progress for each course
            await fetchCoursesProgress(coursesList);
        } else {
          setError(data.message || 'حدث خطأ أثناء جلب الباقات');
          setCourses([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('Error fetching courses:', err);
        setError('حدث خطأ أثناء جلب الباقات');
        setCourses([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    })();
    
    // Store the promise and clean it up when done
    pendingRequests.set(requestKey, requestPromise);
    
    try {
      await requestPromise;
    } finally {
      pendingRequests.delete(requestKey);
    }
  };

  // Fetch progress for enrolled courses
  const fetchCoursesProgress = async (coursesList: Course[]) => {
    if (!currentUser?.id) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      // Fetch progress for all courses in parallel
      const progressPromises = coursesList.map(async (course) => {
        try {
          const response = await axios.get<{ success: boolean; data?: CourseDetails }>(
            `${BASE_URL}api/Course/tree-course-with-progress`,
            {
              params: { courseid: course.id },
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...getTenantHeaders()
              }
            }
          );

          if (response.data.success && response.data.data) {
            const courseDetails = response.data.data;
            
            // Calculate progress percentage
            let totalLessons = 0;
            let completedLessons = 0;
            
            courseDetails.units?.forEach(unit => {
              unit.lessons?.forEach(lesson => {
                totalLessons++;
                if ((lesson.type === 'Video' && lesson.isCompleted) || 
                    (lesson.type === 'Quiz' && lesson.isQuizSubmitted) ||
                    (lesson.type === 'Assignment' && lesson.isAssignmentSubmitted) ||
                    (lesson.type === 'File' && lesson.isFileOpened)) {
                  completedLessons++;
                }
              });
            });
            
            const progress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
            
            return { courseId: course.id, progress };
          }
        } catch (err) {
          console.error(`Error fetching progress for course ${course.id}:`, err);
          return { courseId: course.id, progress: 0 };
        }
        
        return { courseId: course.id, progress: 0 };
      });

      const progressResults = await Promise.all(progressPromises);
      
      // Update courses with progress
      setCourses(prevCourses => 
        prevCourses.map(course => {
          const progressData = progressResults.find(p => p.courseId === course.id);
          return {
            ...course,
            progress: progressData?.progress ?? 0
          };
        })
      );
    } catch (err) {
      console.error('Error fetching courses progress:', err);
    }
  };

  const handleViewCurriculum = (courseId: string) => {
    navigate(`/course/${courseId}/curriculum`);
  };

  // Fetch course curriculum
  const fetchCourseCurriculum = async (courseId: string, autoExpand: boolean = false) => {
    if (courseCurriculums.has(courseId)) {
      // Already loaded
      if (autoExpand && !expandedCourseId.has(courseId)) {
        setExpandedCourseId(prev => new Set(prev).add(courseId));
        // Expand all units by default
        const curriculum = courseCurriculums.get(courseId);
        if (curriculum) {
          const unitIds = curriculum.units.map((_, index) => index);
          setExpandedUnits(prev => {
            const newMap = new Map(prev);
            newMap.set(courseId, new Set(unitIds));
            return newMap;
          });
        }
      }
      return;
    }

    // Add to loading set
    setLoadingCurriculums(prev => new Set(prev).add(courseId));

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await axios.get<{ success: boolean; data?: CourseDetails }>(
        `${BASE_URL}api/Course/tree-course-with-progress`,
        {
          params: { courseid: courseId },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...getTenantHeaders()
          }
        }
      );

      if (response.data.success && response.data.data) {
        const curriculum = response.data.data;
        setCourseCurriculums(prev => {
          const newMap = new Map(prev);
          newMap.set(courseId, curriculum);
          return newMap;
        });
        
        if (autoExpand) {
          setExpandedCourseId(prev => new Set(prev).add(courseId));
          // Expand all units by default
          const unitIds = curriculum.units.map((_, index) => index);
          setExpandedUnits(prev => {
            const newMap = new Map(prev);
            newMap.set(courseId, new Set(unitIds));
            return newMap;
          });
        }
      }
    } catch (err) {
      console.error(`Error fetching curriculum for course ${courseId}:`, err);
    } finally {
      setLoadingCurriculums(prev => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    }
  };

  // Toggle curriculum dropdown
  const toggleCurriculum = (courseId: string) => {
    if (expandedCourseId.has(courseId)) {
      setExpandedCourseId(prev => {
        const newSet = new Set(prev);
        newSet.delete(courseId);
        return newSet;
      });
    } else {
      fetchCourseCurriculum(courseId, true);
    }
  };

  // Toggle unit expansion
  const toggleUnit = (courseId: string, unitIndex: number) => {
    setExpandedUnits(prev => {
      const newMap = new Map(prev);
      const courseUnits = newMap.get(courseId) || new Set<number>();
      const newCourseUnits = new Set(courseUnits);
      
      if (newCourseUnits.has(unitIndex)) {
        newCourseUnits.delete(unitIndex);
      } else {
        newCourseUnits.add(unitIndex);
      }
      
      newMap.set(courseId, newCourseUnits);
      return newMap;
    });
  };

  // Get lesson type icon
  const getLessonTypeIcon = (lesson: Lesson) => {
    switch (lesson.type) {
      case 'Video':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Quiz':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'Assignment':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      case 'File':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={
        embedded
          ? 'py-2 px-4 sm:px-6 lg:px-8'
          : 'min-h-screen bg-[#F4F6FA] py-8 px-4 sm:px-6 lg:px-8'
      }
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {!embedded && (
        <div className="text-center mb-8 sm:mb-10 sm:text-start">
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700 sm:text-sm">
            <BookMarked className="w-3.5 h-3.5" />
            مساحتك التعليمية
          </span>
          <h1 className="mb-2 text-2xl font-bold text-[#001848] font-cairo-bold sm:text-3xl">كورساتي</h1>
          <p className="text-sm text-slate-500 sm:text-base">
            {totalCount > 0
              ? `عندك ${totalCount} ${totalCount === 1 ? 'كورس' : 'كورسات'} — كمّل من حيث وقفت`
              : 'اكتشف كورساتك المسجّلة وابدأ رحلة التعلم'}
          </p>

          <MyProgressStatsSection
            stats={studentStats}
            videoProgress={overallProgress}
            quizProgress={quizProgress}
            loading={loadingStats}
          />
        </div>
        )}

        {embedded && (
          <div className="mb-5 sm:mb-6">
            <MyProgressStatsSection
              stats={studentStats}
              videoProgress={overallProgress}
              quizProgress={quizProgress}
              loading={loadingStats}
            />
          </div>
        )}

        {loading ? (
          <div className="flex flex-col justify-center items-center min-h-[400px] gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-[#001848]" />
            <p className="text-sm text-[#001848] font-medium">جاري تحميل كورساتك...</p>
          </div>
        ) : error ? (
          <div className="text-center text-red-700 bg-red-50 border border-red-200 rounded-2xl p-6">
            {error}
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-14 px-6 rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-[#001848]">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-[#001848]">لا توجد كورسات مسجّلة بعد</h3>
            <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">سجّل في كورس جديد وابدأ رحلتك مع أوائل مصر</p>
            <Link
              to="/student-portal/browse"
              className="inline-flex mt-6 items-center gap-2 px-6 py-2.5 rounded-xl bg-gold-500 text-white font-semibold hover:bg-gold-400 transition"
            >
              تصفّح الكورسات
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 sm:gap-6">
              {courses.map((course) => {
                const curriculum = courseCurriculums.get(course.id);
                const isExpanded = expandedCourseId.has(course.id);
                const isLoadingCurriculum = loadingCurriculums.has(course.id);
                const courseExpandedUnits = expandedUnits.get(course.id) || new Set<number>();

                return (
                  <EnrolledCourseCard
                    key={course.id}
                    course={course}
                    imageSrc={getCourseImageSrc(course)}
                    curriculum={curriculum}
                    isExpanded={isExpanded}
                    isLoadingCurriculum={isLoadingCurriculum}
                    expandedUnitIndexes={courseExpandedUnits}
                    onToggleCurriculum={() => toggleCurriculum(course.id)}
                    onToggleUnit={(unitIndex) => toggleUnit(course.id, unitIndex)}
                    onContinue={() => handleViewCurriculum(course.id)}
                    getLessonTypeIcon={getLessonTypeIcon}
                  />
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex justify-center items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                    currentPage === 1
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                      : 'border border-slate-200 bg-white text-[#001848] hover:border-gold-400'
                  }`}
                >
                  السابق
                </button>
                <span className="px-4 py-2 rounded-xl text-sm font-bold bg-[#001848] text-white">
                  {currentPage} من {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                    currentPage === totalPages
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                      : 'border border-slate-200 bg-white text-[#001848] hover:border-gold-400'
                  }`}
                >
                  التالي
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EnrolledCourses; 